import { useState } from "react";
import { type Language } from "../lib/translations";
import { Button } from "./ui/button";
import { Search, RotateCcw, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";

interface BlogSearchFiltersProps {
  lang: Language;
  searchInput: string;
  setSearchInput: (val: string) => void;
  searchQuery: string;
  appliedFilters: string[];
  selectedFilters: string[];
  toggleFilter: (cat: string) => void;
  allCategories: string[];
  handleSearchSubmit: (e: React.FormEvent) => void;
  handleApplyFilters: () => void;
  handleReset: () => void;
  sortBy: string;
  setSortBy: (val: string) => void;
}

interface SearchFormProps {
  lang: Language;
  searchInput: string;
  setSearchInput: (val: string) => void;
  searchQuery: string;
  appliedFilters: string[];
  selectedFilters: string[];
  handleSearchSubmit: (e: React.FormEvent) => void;
  handleReset: () => void;
  isCollapsed: boolean;
  setIsCollapsed: (val: boolean) => void;
}

function BlogSearchForm({
  lang,
  searchInput,
  setSearchInput,
  searchQuery,
  appliedFilters,
  selectedFilters,
  handleSearchSubmit,
  handleReset,
  isCollapsed,
  setIsCollapsed,
}: SearchFormProps) {
  const totalActive = selectedFilters.length || appliedFilters.length;
  const showReset = searchQuery || appliedFilters.length > 0 || searchInput || selectedFilters.length > 0;

  return (
    <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
      <div className="flex-1 relative flex items-center">
        <Search className="absolute left-3 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          placeholder={lang === "tr" ? "Bloglarda ara..." : "Search articles..."}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full h-8 bg-background/50 border border-border/60 focus:border-brand-primary rounded-xl pl-9 pr-3 text-xs outline-none focus:ring-1 focus:ring-brand-primary text-foreground"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`h-8 text-xs font-semibold px-3 flex items-center gap-1.5 transition-colors border border-border/60 rounded-xl ${!isCollapsed || totalActive > 0
              ? "border-brand-primary text-brand-primary bg-brand-primary/5"
              : "text-muted-foreground hover:text-foreground"
            }`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span>{lang === "tr" ? "Filtrele" : "Filters"}</span>
          {totalActive > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-primary text-white text-[9px] px-1 font-bold">
              {totalActive}
            </span>
          )}
          {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        </Button>

        <Button type="submit" className="bg-brand-primary text-white hover:bg-brand-primary/95 font-semibold px-4 shrink-0 h-8 text-xs rounded-xl">
          {lang === "tr" ? "Ara" : "Search"}
        </Button>

        {showReset && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleReset}
            className="text-muted-foreground hover:text-foreground font-semibold px-3 shrink-0 h-8 text-xs flex items-center gap-1 rounded-xl"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>{lang === "tr" ? "Sıfırla" : "Reset"}</span>
          </Button>
        )}
      </div>
    </form>
  );
}

interface CollapsiblePanelProps {
  lang: Language;
  sortBy: string;
  setSortBy: (val: string) => void;
  allCategories: string[];
  selectedFilters: string[];
  toggleFilter: (cat: string) => void;
  handleApplyFilters: () => void;
}

function BlogCollapsiblePanel({
  lang,
  sortBy,
  setSortBy,
  allCategories,
  selectedFilters,
  toggleFilter,
  handleApplyFilters,
}: CollapsiblePanelProps) {
  return (
    <div className="space-y-4 border-t border-border/20 pt-4 text-left animate-slide-down">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="blog-sort" className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            {lang === "tr" ? "Sıralama:" : "Sort By:"}
          </label>
          <select
            id="blog-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-8 bg-background/50 border border-border/60 focus:border-brand-primary rounded-xl px-2.5 text-xs outline-none focus:ring-1 focus:ring-brand-primary text-foreground"
          >
            <option value="newest">{lang === "tr" ? "En Yeni" : "Newest"}</option>
            <option value="oldest">{lang === "tr" ? "En Eski" : "Oldest"}</option>
            <option value="alphabetical">{lang === "tr" ? "Alfabetik" : "Alphabetical"}</option>
          </select>
        </div>

        {allCategories.length > 0 && (
          <Button
            type="button"
            onClick={handleApplyFilters}
            className="h-8 text-xs bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold px-4 rounded-xl"
          >
            {lang === "tr" ? "Filtreleri Uygula" : "Apply Filters"}
          </Button>
        )}
      </div>

      {allCategories.length > 0 && (
        <div className="space-y-2">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">
            {lang === "tr" ? "Kategori Seçin" : "Select Categories"}
          </span>
          <div className="flex flex-wrap gap-2 pt-0.5">
            {allCategories.map((cat) => {
              const isSelected = selectedFilters.includes(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleFilter(cat)}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition-all duration-200 ${isSelected
                      ? "bg-brand-primary/15 border-brand-primary text-brand-primary font-semibold shadow-sm shadow-brand-primary/10"
                      : "bg-background/40 border-border/40 text-muted-foreground hover:border-border/80"
                    }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function BlogSearchFilters(props: BlogSearchFiltersProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <section className="bg-card/30 backdrop-blur-md border border-border/40 rounded-2xl p-4 space-y-4">
      <BlogSearchForm
        lang={props.lang}
        searchInput={props.searchInput}
        setSearchInput={props.setSearchInput}
        searchQuery={props.searchQuery}
        appliedFilters={props.appliedFilters}
        selectedFilters={props.selectedFilters}
        handleSearchSubmit={props.handleSearchSubmit}
        handleReset={props.handleReset}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />
      {!isCollapsed && (
        <BlogCollapsiblePanel
          lang={props.lang}
          sortBy={props.sortBy}
          setSortBy={props.setSortBy}
          allCategories={props.allCategories}
          selectedFilters={props.selectedFilters}
          toggleFilter={props.toggleFilter}
          handleApplyFilters={props.handleApplyFilters}
        />
      )}
    </section>
  );
}
